import React from 'react';
import { formatINR } from '../../lib/gst';
import { amountInWords } from '../../lib/amountInWords';
import { useLanguage } from '../../contexts/LanguageContext';

export default function TaxSummary({ taxSummary }) {
  const { t } = useLanguage();
  if (!taxSummary) return null;
  const { subtotal, totalDiscount, taxableAmount, cgst, sgst, igst, grandTotal, isInterState } = taxSummary;

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="tax-summary-section">
      <div className="mb-4">
        <div className="flex items-center justify-between sm:flex-row">
          <h3 className="font-heading text-lg font-bold text-slate-900">{t('taxSummary')}</h3>
          <span className={`hidden sm:inline text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded ${isInterState ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="tax-type-indicator">
            {isInterState ? 'INTER-STATE · IGST' : 'INTRA-STATE · CGST + SGST'}
          </span>
        </div>
        <span className={`sm:hidden inline-block mt-1 text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded ${isInterState ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {isInterState ? 'INTER-STATE · IGST' : 'INTRA-STATE · CGST + SGST'}
        </span>
      </div>

      <dl className="space-y-2 text-sm tabular-nums">
        <div className="flex justify-between">
          <dt className="text-slate-600">{t('subtotal')}</dt>
          <dd className="font-medium" data-testid="summary-subtotal">{formatINR(subtotal)}</dd>
        </div>
        {totalDiscount > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-600">{t('discount')}</dt>
            <dd className="font-medium text-red-600" data-testid="summary-discount">− {formatINR(totalDiscount)}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-slate-600">{t('taxableAmount')}</dt>
          <dd className="font-medium" data-testid="summary-taxable">{formatINR(taxableAmount)}</dd>
        </div>
        {isInterState ? (
          <div className="flex justify-between">
            <dt className="text-slate-600">IGST</dt>
            <dd className="font-medium" data-testid="summary-igst">{formatINR(igst)}</dd>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <dt className="text-slate-600">CGST</dt>
              <dd className="font-medium" data-testid="summary-cgst">{formatINR(cgst)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-600">SGST</dt>
              <dd className="font-medium" data-testid="summary-sgst">{formatINR(sgst)}</dd>
            </div>
          </>
        )}
        <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between items-baseline">
          <dt className="font-heading font-bold text-slate-900">{t('grandTotal')}</dt>
          <dd className="font-heading font-black text-2xl text-orange-600" data-testid="summary-grand-total">{formatINR(grandTotal)}</dd>
        </div>
      </dl>

      <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400 mb-1">{t('amountInWords')}</div>
        <div className="text-sm italic text-slate-700" data-testid="summary-amount-words">{amountInWords(grandTotal)}</div>
      </div>
    </section>
  );
}
